/**
 * Caractéristiques lisibles d'un châssis équipé (hangar, armurerie) : calculées sur les réglages effectifs,
 * donc toujours cohérentes avec ce que la simulation utilisera.
 */
import type { GameData, ShipId, SlotId } from '../data';
import { applyLoadout, type Loadout } from '../sim/loadout';

export interface LoadoutStats {
  hull: number;
  shield: number;
  shieldDelay: number;
  cruise: number;
  boost: number;
  agility: number;
  /** Dégâts par seconde du laser, tous faisceaux au but (0 sans laser). */
  laserDps: number;
  laserRange: number;
  missiles: number;
  missileDamage: number;
  swarmBurst: number;
  boostSeconds: number;
  radar: number;
  emergencyRepair: number;
  /** Durée de charge du module de saut (0 sans module). */
  jumpCharge: number;
}

export function loadoutStats(data: GameData, ship: ShipId, loadout: Loadout): LoadoutStats {
  const { data: d, equipment } = applyLoadout(data, ship, loadout);
  const p = d.ships.ships[ship];
  const laser = p.laser;
  return {
    hull: Math.round(p.health),
    shield: equipment.shield?.capacity ?? 0,
    shieldDelay: equipment.shield?.regenDelay ?? 0,
    cruise: Math.round(p.cruiseMaxSpeed),
    boost: Math.round(p.boostMaxSpeed),
    agility: (p.lateralThrustScale + p.yawScale + p.pitchScale + p.rollScale) / 4,
    laserDps: laser ? (laser.damage * (laser.pellets ?? 1)) / laser.interval : 0,
    laserRange: laser?.range ?? 0,
    missiles: p.missiles.max,
    missileDamage: Math.round(d.weapons.missile.damage),
    swarmBurst: p.swarm.max > 0 ? p.swarm.burst : 0,
    boostSeconds: d.ships.flight.boostEnergy.capacity,
    radar: Math.round(d.hud.radarRange),
    emergencyRepair: equipment.emergencyRepair?.amount ?? 0,
    jumpCharge: equipment.jumpDrive?.charge ?? 0,
  };
}

const LABELS: { key: keyof LoadoutStats; label: string; format: (v: number) => string }[] = [
  { key: 'hull', label: 'Coque', format: (v) => String(v) },
  { key: 'shield', label: 'Bouclier', format: (v) => String(v) },
  { key: 'shieldDelay', label: 'Délai du bouclier', format: (v) => `${v.toLocaleString('fr-FR')} s` },
  { key: 'cruise', label: 'Croisière', format: (v) => `${v} m/s` },
  { key: 'boost', label: 'Boost', format: (v) => `${v} m/s` },
  { key: 'agility', label: 'Agilité', format: (v) => `${Math.round(v * 100)} %` },
  { key: 'laserDps', label: 'Canons', format: (v) => `${Math.round(v)} dég./s` },
  { key: 'laserRange', label: 'Portée', format: (v) => `${v} m` },
  { key: 'missiles', label: 'Missiles', format: (v) => String(v) },
  { key: 'missileDamage', label: 'Dégâts missile', format: (v) => String(v) },
  { key: 'swarmBurst', label: 'Salve de nuée', format: (v) => String(v) },
  { key: 'boostSeconds', label: 'Jauge de boost', format: (v) => `${v.toFixed(1).replace('.', ',')} s` },
  { key: 'radar', label: 'Radar', format: (v) => `${v} m` },
  { key: 'emergencyRepair', label: 'Réparation d’urgence', format: (v) => (v > 0 ? `+${v}` : 'aucune') },
  { key: 'jumpCharge', label: 'Charge du saut', format: (v) => (v > 0 ? `${v.toLocaleString('fr-FR')} s` : 'aucun') },
];

export interface StatDelta {
  label: string;
  from: string;
  to: string;
  /** +1 amélioration, −1 dégradation (le délai du bouclier est meilleur quand il baisse). */
  sign: number;
}

/** Ce qui change si l'on monte `itemId` sur `slot` à la place de l'objet actuel. */
export function itemDeltas(data: GameData, ship: ShipId, loadout: Loadout, slot: SlotId, itemId: string): StatDelta[] {
  const before = loadoutStats(data, ship, loadout);
  const after = loadoutStats(data, ship, { ...loadout, [slot]: itemId });
  const out: StatDelta[] = [];
  for (const { key, label, format } of LABELS) {
    const a = before[key], b = after[key];
    if (Math.abs(a - b) < 1e-6) continue;
    // Délai du bouclier et charge du saut : meilleurs quand ils baissent (sauf passage de « aucun » à un module).
    const better = key === 'shieldDelay' || (key === 'jumpCharge' && a > 0 && b > 0) ? b < a : b > a;
    out.push({ label, from: format(a), to: format(b), sign: better ? 1 : -1 });
  }
  return out;
}
